import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface CpmTooltipProps {
    /** Optional heading — when set, the bubble uses the titled (title + sentence) layout. */
    title?: string;
    /** Tooltip body. Omit / pass falsy to render children with no tooltip wiring. */
    content?: ReactNode;
    children: ReactNode;
    /** Which side of the trigger the tooltip opens on. Default: 'top'. */
    side?: 'top' | 'bottom';
    /** Horizontal alignment of the tooltip relative to the trigger. Default: 'start'. */
    align?: 'start' | 'center' | 'end';
    className?: string;
    maxWidth?: number;
    /** Hug the trigger (info icons). Default `w-full` is for table-cell truncation. */
    fit?: boolean;
}

/**
 * Design-system hover tooltip — dark navy pill, dropdown shadow + pop-in
 * animation to match the app's other floating surfaces (CpmFilterDropdown,
 * CpmStoreBrandFilter menus). Portals to document.body so overflow-hidden
 * table cards cannot clip it.
 */
export default function CpmTooltip({
    title,
    content,
    children,
    side = 'top',
    align = 'start',
    className = '',
    maxWidth = 320,
    fit = false,
}: CpmTooltipProps) {
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLSpanElement>(null);
    const tipRef = useRef<HTMLSpanElement>(null);
    const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
    const id = useId();

    useLayoutEffect(() => {
        if (!open) {
            setCoords(null);
            return;
        }
        const update = () => {
            const trigger = triggerRef.current;
            const tip = tipRef.current;
            if (!trigger) return;
            const rect = trigger.getBoundingClientRect();
            const tipWidth = tip?.offsetWidth ?? Math.min(maxWidth, 240);
            const tipHeight = tip?.offsetHeight ?? 80;
            const gap = 8;

            const spaceAbove = rect.top - gap;
            const spaceBelow = window.innerHeight - rect.bottom - gap;
            const placeTop =
                side === 'top'
                    ? spaceAbove >= tipHeight || spaceAbove >= spaceBelow
                    : spaceBelow < tipHeight && spaceAbove > spaceBelow;

            const top = placeTop ? rect.top - gap - tipHeight : rect.bottom + gap;
            let left =
                align === 'center'
                    ? rect.left + rect.width / 2 - tipWidth / 2
                    : align === 'end'
                      ? rect.right - tipWidth
                      : rect.left;
            left = Math.max(8, Math.min(left, window.innerWidth - tipWidth - 8));

            setCoords({ top, left });
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [open, side, align, maxWidth, content, title]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    if (!content) {
        return <>{children}</>;
    }

    return (
        <span
            ref={triggerRef}
            className={[
                fit ? 'relative inline-flex shrink-0' : 'relative block w-full min-w-0',
                className,
            ].join(' ')}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            aria-describedby={open ? id : undefined}
        >
            {children}
            {open && typeof document !== 'undefined'
                ? createPortal(
                      <span
                          ref={tipRef}
                          id={id}
                          role="tooltip"
                          className="cpm-theme pointer-events-none cpm-animate-pop-in"
                          style={{
                              position: 'fixed',
                              top: coords?.top ?? -9999,
                              left: coords?.left ?? -9999,
                              zIndex: 100,
                              maxWidth,
                              width: 'max-content',
                              padding: title ? '9px 11px' : '8px 11px',
                              borderRadius: title ? 'var(--cpm-radius-8)' : 'var(--cpm-radius-10)',
                              background: 'var(--cpm-brand-navy)',
                              color: '#fff',
                              fontSize: 11.5,
                              fontWeight: title ? 500 : 700,
                              lineHeight: 1.45,
                              whiteSpace: 'normal',
                              boxShadow: 'var(--cpm-shadow-dropdown)',
                              visibility: coords ? 'visible' : 'hidden',
                          }}
                      >
                          {title ? (
                              <>
                                  <span
                                      style={{
                                          display: 'block',
                                          fontSize: 11.5,
                                          fontWeight: 800,
                                          letterSpacing: '0.01em',
                                          marginBottom: 3,
                                      }}
                                  >
                                      {title}
                                  </span>
                                  <span
                                      style={{
                                          display: 'block',
                                          fontSize: 11.5,
                                          fontWeight: 500,
                                          lineHeight: 1.5,
                                          color: 'rgba(255,255,255,0.86)',
                                      }}
                                  >
                                      {content}
                                  </span>
                              </>
                          ) : (
                              content
                          )}
                      </span>,
                      document.body
                  )
                : null}
        </span>
    );
}
